import {useEffect, useRef, useState, type FormEvent} from "react";
import {useTranslation} from "react-i18next";
import {ApiError, OperationKey, browserLogin, browserRegister, type BrowserTokens, type CaptchaResponse, type LoginInput, type RegisterInput} from "../../../shared/api/client";
import {supportPhoneChangeEnabled} from "../../../shared/config/runtime";
import {localizeAPIError} from "../../../shared/i18n/api-errors";
import {sessionStore} from "../../../shared/session/session-store";
import {Field} from "../../../shared/ui/field";
import {CaptchaChallenge, captchaChallengeFromError, type CaptchaChallengeDescriptor} from "./captcha-challenge";
import {PhoneVerification} from "./phone-verification";

export type AuthMode = "signup" | "signin";

interface AuthFormProps {
  initialMode: AuthMode;
  headingLevel?: "h1" | "h2";
  headingId?: string;
  onAuthenticated?: () => void;
  passwordResetComplete?: boolean;
}

export function AuthForm({initialMode, headingLevel = "h1", headingId, onAuthenticated, passwordResetComplete = false}: AuthFormProps) {
  const {t} = useTranslation();
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [busy, setBusy] = useState(false);
  const [complete, setComplete] = useState(false);
  const [phoneVerification, setPhoneVerification] = useState<{phone: string; tokens: BrowserTokens} | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [captchaChallenge, setCaptchaChallenge] = useState<CaptchaChallengeDescriptor | null>(null);
  const [captchaResponse, setCaptchaResponse] = useState<CaptchaResponse | null>(null);
  const [captchaAttempt, setCaptchaAttempt] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const signupOperation = useRef(new OperationKey());
  const Heading = headingLevel;
  const localizedError = error ? localizeAPIError(error, mode, t) : null;

  useEffect(() => {
    if (!error) return;
    const frame = requestAnimationFrame(() => {
      const invalid = formRef.current?.querySelector<HTMLElement>("[aria-invalid='true']");
      (invalid ?? errorRef.current)?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [error]);

  const switchMode = (next: AuthMode) => {
    setMode(next);
    setError(null);
    setComplete(false);
    setPhoneVerification(null);
    setCaptchaChallenge(null);
    setCaptchaResponse(null);
    setCaptchaAttempt((value) => value + 1);
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const form = new FormData(event.currentTarget);
    let submittedCaptcha = false;
    try {
      if (mode === "signin") {
        if (captchaChallenge && !captchaResponse) return;
        submittedCaptcha = Boolean(captchaResponse);
        const input: LoginInput = {login: required(form, "login"), password: required(form, "password")};
        if (captchaResponse) {
          input.captcha = captchaResponse;
        }
        const tokens = await browserLogin(input, new OperationKey());
        await sessionStore.accept(tokens);
        onAuthenticated?.();
        return;
      }

      const firstName = required(form, "first_name");
      const email = text(form, "email");
      const phone = text(form, "phone");
      if (!email && !phone) {
        throw fieldError("client_contact_required", {
          email: "client_contact_required",
          phone: "client_contact_required",
        });
      }
      const birthDate = required(form, "birth_date");
      const password = required(form, "password");
      if (password !== required(form, "password_confirmation")) {
        throw fieldError("password_confirmation", "client_password_mismatch");
      }
      if (form.get("terms_accepted") !== "on") {
        throw fieldError("terms_accepted", "client_required");
      }
      const input: RegisterInput = {
        first_name: firstName,
        birth_date: birthDate,
        password,
        terms_accepted: true,
      };
      const lastName = text(form, "last_name");
      if (lastName) input.last_name = lastName;
      if (email) input.email = email;
      if (phone) input.phone = phone;
      const result = await browserRegister(input, signupOperation.current);
      if (phone && result.tokens) {
        setPhoneVerification({phone, tokens: result.tokens});
      } else if (result.tokens) {
        await sessionStore.accept(result.tokens);
        onAuthenticated?.();
      } else if (!email) {
        throw new ApiError(500, errorEnvelope("internal"));
      } else {
        setComplete(true);
      }
    } catch (caught) {
      const nextError = caught instanceof ApiError ? caught : new ApiError(0, errorEnvelope("client_error"));
      if (mode === "signin") {
        const nextChallenge = captchaChallengeFromError(nextError);
        if (nextChallenge) setCaptchaChallenge(nextChallenge);
      }
      setError(nextError);
    } finally {
      if (submittedCaptcha) {
        setCaptchaResponse(null);
        setCaptchaAttempt((value) => value + 1);
      }
      setBusy(false);
    }
  };

  if (phoneVerification) {
    return <PhoneVerification
      phone={phoneVerification.phone}
      tokens={phoneVerification.tokens}
      headingLevel={headingLevel}
      headingId={headingId}
      onVerified={async () => {
        await sessionStore.accept(phoneVerification.tokens);
        onAuthenticated?.();
      }}
    />;
  }

  if (complete) {
    return <section className="auth-complete" aria-live="polite">
      <Heading id={headingId}>{t("auth.completeTitle")}</Heading>
      <p>{t("auth.completeBody")}</p>
      <button className="button secondary" type="button" onClick={() => switchMode("signin")}>{t("auth.goToSignin")}</button>
    </section>;
  }

  return (
    <section className="auth-card">
      <div className="mode-tabs" aria-label={t("auth.chooseAction")}>
        <button type="button" aria-pressed={mode === "signup"} onClick={() => switchMode("signup")}>{t("auth.signupTab")}</button>
        <button type="button" aria-pressed={mode === "signin"} onClick={() => switchMode("signin")}>{t("auth.signinTab")}</button>
      </div>
      <Heading id={headingId}>{mode === "signup" ? t("auth.signupTitle") : t("auth.signinTitle")}</Heading>
      {mode === "signin" && passwordResetComplete && <p className="auth-status" role="status" aria-live="polite">{t("auth.passwordReset.signinSuccess")}</p>}
      <form ref={formRef} onSubmit={submit} noValidate aria-busy={busy}>
        {mode === "signup" ? <SignupFields errors={localizedError?.fields ?? {}} /> : <SigninFields errors={localizedError?.fields ?? {}} />}
        {mode === "signin" && captchaChallenge && <CaptchaChallenge
          challenge={captchaChallenge}
          attempt={captchaAttempt}
          onResponse={setCaptchaResponse}
        />}
        {localizedError && <div className="form-error" role="alert" tabIndex={-1} ref={errorRef}>
          <p>{localizedError.message}</p>
          {localizedError.requestId && <small>{t("common.requestId")}: <code>{localizedError.requestId}</code></small>}
        </div>}
        <button className="button primary" type="submit" disabled={busy || (mode === "signin" && Boolean(captchaChallenge) && !captchaResponse)}>{busy ? t("auth.sending") : mode === "signup" ? t("auth.register") : t("auth.signIn")}</button>
      </form>
    </section>
  );
}

function SignupFields({errors}: {errors: Record<string, string>}) {
  const {t} = useTranslation();
  const termsErrorId = errors.terms_accepted ? "terms-accepted-error" : undefined;
  return <>
    <div className="field-row">
      <Field name="first_name" label={t("auth.firstName")} autoComplete="given-name" required error={errors.first_name} autoFocus />
      <Field name="last_name" label={t("auth.lastName")} autoComplete="family-name" error={errors.last_name} />
    </div>
    <Field name="email" type="email" label={t("auth.emailOptional")} autoComplete="email" error={errors.email} />
    <Field name="phone" type="tel" label={t("auth.phoneOptional")} autoComplete="tel" error={errors.phone} />
    <Field name="birth_date" type="date" label={t("auth.birthDate")} autoComplete="bday" required error={errors.birth_date} />
    <Field name="password" type="password" label={t("auth.password")} autoComplete="new-password" minLength={8} required error={errors.password} />
    <Field name="password_confirmation" type="password" label={t("auth.passwordConfirmation")} autoComplete="new-password" minLength={8} required error={errors.password_confirmation} />
    <label className="checkbox-field" htmlFor="terms-accepted">
      <input id="terms-accepted" name="terms_accepted" type="checkbox" required aria-invalid={Boolean(errors.terms_accepted)} aria-describedby={termsErrorId} />
      <span>{t("auth.termsPrefix")} <a href="/site/terms" target="_blank" rel="noreferrer">{t("auth.termsLink")}</a></span>
    </label>
    {errors.terms_accepted && <small className="field-error" id={termsErrorId}>{errors.terms_accepted}</small>}
  </>;
}

function SigninFields({errors}: {errors: Record<string, string>}) {
  const {t} = useTranslation();
  return <>
    <Field name="login" label={t("auth.login")} autoComplete="username" required error={errors.login} autoFocus />
    <Field name="password" type="password" label={t("auth.password")} autoComplete="current-password" required error={errors.password} />
    <a className="auth-secondary-link" href="/forgot-password">{t("auth.passwordReset.forgot")}</a>
    {supportPhoneChangeEnabled() && <a className="auth-secondary-link" href="/phone-recovery">{t("phoneRecovery.title")}</a>}
  </>;
}

function text(form: FormData, name: string): string {
  const value = form.get(name);
  return typeof value === "string" ? value.trim() : "";
}

function required(form: FormData, name: string): string {
  const value = text(form, name);
  if (!value) throw fieldError(name, "client_required");
  return value;
}

function fieldError(name: string, code: string): ApiError;
function fieldError(code: string, fields: Record<string, string>): ApiError;
function fieldError(nameOrCode: string, codeOrFields: string | Record<string, string>): ApiError {
  if (typeof codeOrFields === "string") {
    return new ApiError(422, errorEnvelope(codeOrFields, {[nameOrCode]: codeOrFields}));
  }
  return new ApiError(422, errorEnvelope(nameOrCode, codeOrFields));
}

function errorEnvelope(code: string, fields: Record<string, string> = {}) {
  return {error: {code, message: "", request_id: "", fields}};
}
